import { z } from 'zod';
import { toModelId, toProjectId, toProjectMemberId, toTenantId } from '@/shared/types/common.types';
import { tolerantEnum, v1DateSchema } from '@/shared/utils/api/serverPrimitives';
import { HEALTH_STATUSES } from '../types/rollup.types';
import {
  MEMBER_STATUSES,
  MODEL_STATUSES,
  PROJECT_ROLES,
  PROJECT_STATUSES,
  TENANT_ROLES,
} from '../types/workspace.types';
import type {
  CursorPage,
  ProjectListPage,
  ProjectMember,
  Tenant,
  WorkspaceProject,
  WorkspaceProjectSummary,
} from '../types/workspace.types';

/**
 * The wire contract for the Core workspace routes (`/api/v1/tenants`, `/api/v1/projects`).
 *
 * These follow the v1 conventions, not the legacy ones in `core-api-compatibility.md`:
 * enums are strings, dates are ISO-8601 UTC with `Z`, ids are GUIDs and collections are
 * cursor pages. An enum value this client does not know becomes `Unknown` rather than
 * failing the whole response, the same tolerance the legacy contracts give numeric enums.
 */
const projectRoleSchema = tolerantEnum(PROJECT_ROLES);
const tenantRoleSchema = tolerantEnum(TENANT_ROLES);
const memberStatusSchema = tolerantEnum(MEMBER_STATUSES);

const personRefSchema = z.object({
  subject: z.string(),
  displayName: z.string(),
});

export function cursorPageSchema<TItem extends z.ZodTypeAny>(item: TItem) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().min(1).nullable(),
  });
}

export const tenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  myRole: tenantRoleSchema,
});

export const projectSummarySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  myRole: projectRoleSchema,
  createdAt: v1DateSchema,
  // Proposed list fields (docs/project-admin-and-list-summary.md); an older Core omits them.
  modelCount: z.number().int().nonnegative().nullish(),
  health: tolerantEnum(HEALTH_STATUSES).nullish(),
  manager: personRefSchema.nullish(),
  status: tolerantEnum(PROJECT_STATUSES).nullish(),
});

export const projectListSchema = cursorPageSchema(projectSummarySchema).extend({
  permissions: z.object({ canCreateProject: z.boolean() }),
});

export const linkedModelSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  modelVersion: z.number().int(),
  status: tolerantEnum(MODEL_STATUSES).nullish(),
  lastSynchronized: v1DateSchema.nullable(),
  /** Proposed: the project member answerable for this model. */
  responsible: personRefSchema.nullish(),
  /** Proposed: how many snapshots the model has. */
  snapshotCount: z.number().int().nonnegative().nullish(),
});

export const projectSchema = projectSummarySchema.extend({
  canViewRestricted: z.boolean(),
  modelIds: z.array(z.number().int()),
  // Absent from a Core that predates model details.
  models: z.array(linkedModelSchema).default([]),
  activeManifestRevisionId: z.string().uuid().nullable(),
  etag: z.string().min(1),
  createdBy: personRefSchema,
});

export const memberSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  subject: z.string().nullable(),
  displayName: z.string().nullable(),
  role: projectRoleSchema,
  canViewRestricted: z.boolean(),
  status: memberStatusSchema,
  addedAt: v1DateSchema,
  /** Proposed: what the person does on this project (engineer, manager, consultant...). */
  position: z.string().nullish(),
  /** Proposed: whether they belong to the organisation or are an outside guest. */
  memberType: z.enum(['Organisation', 'External']).nullish().catch(null),
});

export const tenantPageSchema = cursorPageSchema(tenantSchema);
export const memberPageSchema = cursorPageSchema(memberSchema);

export function toTenant(raw: z.output<typeof tenantSchema>): Tenant {
  return { id: toTenantId(raw.id), name: raw.name, myRole: raw.myRole };
}

export function toProjectSummary(raw: z.output<typeof projectSummarySchema>): WorkspaceProjectSummary {
  return {
    id: toProjectId(raw.id),
    tenantId: toTenantId(raw.tenantId),
    code: raw.code,
    name: raw.name,
    myRole: raw.myRole,
    createdAt: raw.createdAt,
    modelCount: raw.modelCount ?? null,
    health: raw.health ?? null,
    manager: raw.manager ?? null,
    status: raw.status ?? null,
  };
}

export function toProjectListPage(raw: z.output<typeof projectListSchema>): ProjectListPage {
  return {
    items: raw.items.map(toProjectSummary),
    nextCursor: raw.nextCursor,
    permissions: raw.permissions,
  };
}

export function toProject(raw: z.output<typeof projectSchema>): WorkspaceProject {
  return {
    ...toProjectSummary(raw),
    // A project read on its own knows its models even when the list did not say.
    modelCount: raw.modelCount ?? raw.modelIds.length,
    canViewRestricted: raw.canViewRestricted,
    modelIds: raw.modelIds.map(toModelId),
    models: raw.models.map((model) => ({
      id: toModelId(model.id),
      title: model.title,
      modelVersion: model.modelVersion,
      status: model.status ?? null,
      lastSynchronized: model.lastSynchronized,
      responsible: model.responsible ?? null,
      snapshotCount: model.snapshotCount ?? null,
    })),
    activeManifestRevisionId: raw.activeManifestRevisionId,
    etag: raw.etag,
    createdBy: raw.createdBy,
  };
}

export function toMember(raw: z.output<typeof memberSchema>): ProjectMember {
  return {
    id: toProjectMemberId(raw.id),
    email: raw.email,
    subject: raw.subject,
    displayName: raw.displayName,
    role: raw.role,
    canViewRestricted: raw.canViewRestricted,
    status: raw.status,
    addedAt: raw.addedAt,
    position: raw.position ?? null,
    memberType: raw.memberType ?? null,
  };
}

export function toCursorPage<TRaw, TItem>(
  raw: { readonly items: readonly TRaw[]; readonly nextCursor: string | null },
  map: (item: TRaw) => TItem
): CursorPage<TItem> {
  return { items: raw.items.map(map), nextCursor: raw.nextCursor };
}
